// Creator portal — 4-step onboarding roadmap. PivotPhase-2.
// 2026-07-13 — former step 3 "Payout Setup" cut per product decision; the roadmap
// is now 4 steps (profile → contract → schedule call → join community).
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/brand/AppShell";
import { apiClient, useAuth } from "../auth/AuthContext";

const ROUTES = { 1: "/profile", 2: "/contract", 3: null, 4: null };

function StatusPill({ step }) {
  const { done, locked } = step;
  if (done) return <span data-testid={`step-${step.n}-pill`} className="rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-emerald-300">Done</span>;
  if (locked) return <span data-testid={`step-${step.n}-pill`} className="rounded-full border border-brand-text/20 bg-brand-text/5 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-brand-text/40">Locked</span>;
  return <span data-testid={`step-${step.n}-pill`} className="rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-brand-gold">In Progress</span>;
}

export default function Portal() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { document.title = "Portal · Omertàbella"; }, []);

  const load = async () => {
    const r = await apiClient.get("/me/onboarding");
    setState(r.data);
  };
  useEffect(() => { load(); }, []);

  const markDone = async (n) => {
    setBusy(true);
    try { const r = await apiClient.post(`/me/onboarding/step/${n}/complete`); setState(r.data); }
    finally { setBusy(false); }
  };

  if (!state) return <AppShell><div className="p-12 text-center text-brand-text/60">Loading…</div></AppShell>;
  const greeting = (user?.name || user?.email || "creator").split("@")[0];

  return (
    <AppShell>
      <div data-testid="portal-page" className="mx-auto max-w-4xl px-6 py-16">
        <p className="text-xs uppercase tracking-[0.32em] text-brand-gold">Onboarding · Step {Math.min(state.current_step, state.total_steps)}/{state.total_steps}</p>
        <h1 className="mt-4 font-display text-4xl text-brand-text sm:text-5xl">Welcome in, {greeting}.</h1>
        <p className="mt-3 text-sm text-brand-text/60">Your onboarding roadmap. Complete each step to activate your full management contract.</p>

        <div className="mt-10">
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] uppercase tracking-[0.32em] text-brand-text/60">Onboarding Progress</span>
            <span data-testid="portal-progress-percent" className="font-display text-2xl text-brand-gold">{state.percent}%</span>
          </div>
          <div className="mt-2 h-1 w-full overflow-hidden rounded bg-brand-text/10">
            <div
              data-testid="portal-progress-bar"
              className="h-full bg-gradient-to-r from-brand-gold/70 to-brand-gold transition-all duration-700"
              style={{ width: `${state.percent}%` }}
            />
          </div>
          <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-brand-text/40">
            {state.completed_steps} of {state.total_steps} steps complete
          </p>
        </div>

        <div className="mt-10 space-y-4">
          {state.steps.map((s) => (
            <div
              key={s.n}
              data-testid={`portal-step-${s.n}`}
              className={`flex flex-col gap-4 rounded border p-6 transition sm:flex-row sm:items-center ${
                s.done ? "border-emerald-400/30 bg-[#121613]" :
                s.locked ? "border-brand-text/10 bg-[#141414] opacity-60" :
                "border-brand-gold/30 bg-[#1A1A1A]"
              }`}
            >
              <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border font-display text-lg ${s.done ? "border-emerald-400/40 text-emerald-300" : "border-brand-gold/50 text-brand-gold"}`}>
                {String(s.n).padStart(2, "0")}
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-display text-xl text-brand-text">{s.label}</h3>
                  <StatusPill step={s} />
                </div>
                <p className="mt-1 text-sm text-brand-text/60">{s.blurb}</p>
              </div>
              <div className="shrink-0">
                {s.n <= 2 ? (
                  s.done ? (
                    <Link to={ROUTES[s.n]} className="inline-block rounded border border-brand-text/20 px-4 py-2 text-xs uppercase tracking-[0.24em] text-brand-text/70 hover:border-brand-gold/40 hover:text-brand-gold">View</Link>
                  ) : s.locked ? (
                    <span className="inline-block rounded border border-brand-text/10 px-4 py-2 text-xs uppercase tracking-[0.24em] text-brand-text/30">Locked</span>
                  ) : (
                    <Link data-testid={`step-${s.n}-cta`} to={ROUTES[s.n]} className="inline-block rounded border border-brand-gold bg-transparent px-4 py-2 text-xs uppercase tracking-[0.24em] text-brand-gold hover:bg-brand-gold hover:text-brand-bg">
                      {s.n === 1 ? "Open profile" : "Review & sign"}
                    </Link>
                  )
                ) : (
                  s.done ? (
                    <span className="inline-block rounded border border-emerald-400/40 px-4 py-2 text-xs uppercase tracking-[0.24em] text-emerald-300">Marked</span>
                  ) : (
                    <button
                      data-testid={`step-${s.n}-cta`}
                      disabled={busy}
                      onClick={() => markDone(s.n)}
                      className="rounded border border-brand-gold bg-transparent px-4 py-2 text-xs uppercase tracking-[0.24em] text-brand-gold hover:bg-brand-gold hover:text-brand-bg disabled:opacity-50"
                    >
                      {s.n === 3 ? "Mark scheduled" : "Mark joined"}
                    </button>
                  )
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 border-t border-brand-text/10 pt-6 text-xs uppercase tracking-[0.32em] text-brand-text/40">
          Need help? Reach your manager at <a href="mailto:admin@omertabella.com" className="text-brand-gold">admin@omertabella.com</a>
        </div>
      </div>
    </AppShell>
  );
}
